<template>
    <b-row>
        <b-col>
            <b-row v-if="mode == 'associate'">
                <b-col>
                    <b>File Received: </b><span>{{ queueItem.ingested | formatDate("L LT") }}</span>;
                    <b> Source: </b><span>{{ queueItem.source }}</span>
                </b-col>
            </b-row>

            <!-- Select patient -->
            <b-row v-if="mode == 'associate'">
                <b-col>
                    <SearchComponent v-model="selectedPatient" :label="`Select a Patient`" search-u-r-l="/Patient"
                        create-new-object createNewLabel="Create New Patient"
                        @createNewObject="showPatientForm=true"
                        @input="loadDocs" />
                </b-col>
            </b-row>
            <card v-else plain class="mb-3">
                <span><b>Patient: </b>{{ patient.displayNamesForPerson() }}</span>
                <span class="ml-4"><b>DOB: </b>{{ patient.dateOfBirth | formatLocalDate("L") }}</span>
            </card>

            <PatientFormComponent v-if="showPatientForm"
                :button-titles="patientButtonTitles"
                create-new-patient hide-insurance
                @close="closePatientForm"
                @created="onPatientCreated" />

            <!-- Upload component -->
            <card v-if="!listOnly" plain class="mb-4">
                <b-row v-if="mode == 'new'" class="mb-2">
                    <b-col>
                        <label for="uploadFile">Upload File</label>
                        <b-form-file id="uploadFile" v-model="newDocument" accept=".pdf"
                            drop-placeholder="Drop file here..."
                            placeholder="Choose patient's document or drop it here...">
                        </b-form-file>
                    </b-col>
                </b-row>

                <!-- Indicate File Name-->
                <b-row>
                    <b-col>
                        <fg-input v-model="fileName" :class="fileNameInputClass" label="File Name" required></fg-input>
                    </b-col>
                </b-row>

                <b-row v-if="availableTags?.length">
                    <b-col>
                        <label>Tags<span v-if="tagsRequired"> *</span></label>
                        <multiselect v-model="selectedTags" :options="availableTags" multiple />
                    </b-col>
                </b-row>

                <b-row class="mt-3">
                    <b-col>
                        <acceptance-config-selector
                            :enabled.sync="createAcceptanceOnSave"
                            :configuration.sync="selectedAcceptanceConfigObject"
                            :source-type.sync="acceptanceSourceType" />
                    </b-col>
                </b-row>

                <!-- Offered in both modes: the ingestion-queue path that motivates this is 'associate'. -->
                <b-row v-if="taskOnSave" class="mt-3">
                    <b-col>
                        <b-form-checkbox v-model="createTaskOnSave" switch>
                            Create a task for this document
                        </b-form-checkbox>
                    </b-col>
                </b-row>

                <b-row v-if="mode == 'new'" class="mt-3">
                    <b-col>
                        <b-form-checkbox v-model="forwardViaFax" switch>
                            Forward via Fax after upload
                        </b-form-checkbox>
                        <b-collapse :visible="forwardViaFax" class="mt-2">
                            <b-form-group label="Fax Number:" class="mb-2">
                                <b-input v-model="uploadFaxNumber"
                                    :state="uploadFaxNumber ? isValidUploadFaxNumber : null"
                                    placeholder="Enter fax number"></b-input>
                            </b-form-group>
                            <b-form-group label="Note on Fax:" :description="`${uploadFaxNote.length}/2000 characters`">
                                <b-textarea v-model="uploadFaxNote" maxlength="2000" rows="3"></b-textarea>
                            </b-form-group>
                        </b-collapse>
                    </b-col>
                </b-row>

                <b-row v-if="mode == 'associate' && allowProcessAsPrescription" class="mt-3">
                    <b-col>
                        <b-form-checkbox v-model="processAsPrescription" switch>
                            Also process as prescription
                        </b-form-checkbox>
                    </b-col>
                </b-row>

                <!-- Save button @click="saveDocument"-->
                <div v-if="!isUploading" class="mt-3">
                    <b-button :disabled="disableSave" variant="primary" @click="submit">Save Document</b-button>
                    <b-button variant="dark" @click="close">Cancel</b-button>
                </div>
                <div v-else class="mt-3">
                    <b-spinner class="mr-2" label="Spinning"></b-spinner>
                    <strong>Uploading...</strong>
                </div>
            </card>

            <div class="d-flex field-filters flex-wrap mb-2 sticky-top glass-pane py-2">
                <b-badge v-if="documentsHaveTags" :variant="filterTags.length ? 'secondary' : 'primary'" @click="clearFilters">
                    All Documents
                </b-badge>
                <b-badge v-for="i in documentsUniqueTags" :key="i"
                    :variant="!filterTags.includes(i) ? 'secondary' : 'primary'" @click="toggleFilterTag(i)">
                    {{ i }}
                    <span class="count">{{ getDocumentCountForTag(i) }}</span>
                </b-badge>
                <b-form-checkbox v-if="hasViewDeletedPermission && deletedDocumentsCount > 0"
                    v-model="showDeleted"
                    switch
                    class="ml-auto show-deleted-switch"
                    @change="scrollToTable">
                    Show Deleted ({{ deletedDocumentsCount }})
                </b-form-checkbox>
            </div>
            <b-modal id="fax-modal" title="Fax Document" ok-title="Send Fax" @ok="faxDocument" @hidden="clearFaxForm">
                <b-form-group label="Fax Number:">
                    <b-input v-model="faxNumber"></b-input>
                </b-form-group>
                <b-form-group label="Note on Fax:" :description="`${faxNote.length}/2000 characters`">
                    <b-textarea v-model="faxNote" maxlength="2000" rows="4"></b-textarea>
                </b-form-group>
                <b-button variant="outline-primary" size="sm" @click="previewFaxDocument">
                    Preview
                </b-button>
            </b-modal>
            <FaxPreviewModal ref="previewModal" modal-id="patient-document-fax-preview" />
            <PdfAnnotationModal
                v-if="annotatingDocument"
                :image-id="annotatingDocument.imageId"
                modal-id="patient-document-annotation"
                title="Edit Document"
                @saved="onAnnotationSaved"
                @closed="annotatingDocument = null" />
            <PdfChopperModal
                v-if="splittingDocument"
                id="patient-document-split"
                :image-id="splittingDocument.imageId"
                context="patientDocument"
                :patient="splitPatient"
                :source-patient-document-id="splittingDocument.id"
                v-model="showSplitModal"
                @splitComplete="onSplitComplete" />
            <!-- Move Document Modal -->
            <b-modal id="move-doc-modal" title="Move Document to Another Patient"
                @ok="moveDocument" @show="showMoveModal" :ok-disabled="!moveTargetPatient?.id">
                <p>Moving: <strong>{{ documentToMove?.fileName }}</strong></p>

                <SearchComponent v-model="moveTargetPatient" search-u-r-l="/Patient"
                    label="Select Target Patient" />

                <PatientRecentlyViewed @change="moveTargetPatient = $event"
                    :selected="moveTargetPatient" :hide-list="hideMoveRecentList" :disable-patient="patient">
                    <template #title>
                        <a href="#" @click.prevent="hideMoveRecentList = !hideMoveRecentList">
                            <i :class="{ 'fa': true, 'fa-chevron-right': true, 'fa-rotate-90': !hideMoveRecentList }"
                                aria-hidden="true" style="transition: all .2s;"></i>
                            Recently Viewed Patients
                        </a>
                    </template>
                </PatientRecentlyViewed>
            </b-modal>
            <TaskDetails v-if="showCreateTaskModal"
                :id="0"
                :patient-id="patientID"
                :patient-documents="createTaskDocuments"
                @saved="onTaskCreated"
                @closed="onTaskModalClosed" />
            <!-- Documents Grid , row event open file in new tab -->
            <b-table ref="documentsTable" class="documents-table" :empty-text="emptyText" :fields="fields" :items="filteredPatientDocuments"
                show-empty striped primary-key="id" sort-by="created" :sort-desc="true"
                :tbody-tr-class="rowClass">
                <template #cell(fileName)="data">
                    <b-input v-if="editingRowIndex === data.index" v-model="editingPatientDocument.fileName"
                        size="sm"></b-input>
                    <span v-else>
                        <strong>
                            <a href="" @click.prevent="openDocument(data.item)">
                                {{ data.value }}
                            </a>
                        </strong>
                        <b-badge v-if="data.item.deleted" variant="danger" class="ml-2">Deleted</b-badge>
                        <b-badge v-if="data.item.movedToPatientDocumentId" variant="warning" class="ml-1">Moved</b-badge>
                    </span>
                </template>
                <template #cell(selected)="row">
                    <input type="checkbox" :value="row.item.imageId" v-model="selectedImageIds"></input>
                </template>
                <template #cell(view)='data'>
                    <slot name="cell(view)" v-bind:data="data">
                        <div v-if="editingRowIndex === -1" class="table-icon-actions">
                            <template v-if="!data.item.deleted">
                                <!-- Primary actions: View, Edit, Fax -->
                                <a v-b-tooltip.hover href="" title="View" @click.prevent="openDocument(data.item)">
                                    <b-icon icon="eye" variant="primary"></b-icon>
                                </a>
                                <a v-b-tooltip.hover href="" title="Edit"
                                    @click.prevent="startEditDocument(data.item, data.index)">
                                    <b-icon icon="pencil" variant="primary"></b-icon>
                                </a>
                                <a v-b-tooltip.hover href="" title="Fax" @click.prevent="$bvModal.show('fax-modal'); faxImageId = data.item.imageId">
                                    <b-icon icon="share" variant="primary" />
                                </a>
                                <!-- Secondary actions dropdown -->
                                <b-dropdown variant="link" size="sm" no-caret right
                                    class="doc-actions-dropdown">
                                    <template #button-content>
                                        <span class="doc-actions-ellipsis">
                                            <b-icon icon="three-dots" variant="primary"></b-icon>
                                        </span>
                                    </template>
                                    <b-dropdown-item v-if="hasAnnotateOrEditPermission" @click="openAnnotateModal(data.item)">
                                        <b-icon icon="pencil-square" variant="primary" class="mr-2"></b-icon>
                                        Edit Image
                                    </b-dropdown-item>
                                    <b-dropdown-item v-if="hasAnnotateOrEditPermission" @click="openSplitModal(data.item)">
                                        <b-icon icon="scissors" variant="primary" class="mr-2"></b-icon>
                                        Split Document
                                    </b-dropdown-item>
                                    <b-dropdown-item v-if="hasMovePermission" @click="openMoveModal(data.item)">
                                        <b-icon icon="arrow-right-square" variant="primary" class="mr-2"></b-icon>
                                        Move to Another Patient
                                    </b-dropdown-item>
                                    <b-dropdown-item v-if="taskCreation" @click="createTaskForDocument(data.item)">
                                        <b-icon icon="list-task" variant="primary" class="mr-2"></b-icon>
                                        Create Task
                                    </b-dropdown-item>
                                    <b-dropdown-divider v-if="hasDeletePermission"></b-dropdown-divider>
                                    <b-dropdown-item v-if="hasDeletePermission" @click="confirmDeleteDocument(data.item)">
                                        <b-icon icon="trash" variant="primary" class="mr-2"></b-icon>
                                        Delete
                                    </b-dropdown-item>
                                </b-dropdown>
                            </template>
                            <template v-else>
                                <a v-b-tooltip.hover href="" title="View" @click.prevent="openDocument(data.item)">
                                    <b-icon icon="eye" variant="secondary"></b-icon>
                                </a>
                            </template>
                        </div>
                        <div v-if="editingRowIndex === data.index" class="table-icon-actions">
                            <a v-b-tooltip.hover href="" title="Save" @click.prevent="editDocument">
                                <b-icon icon="save" variant="primary"></b-icon>
                            </a>
                            <a v-b-tooltip.hover href="" title="Cancel" @click.prevent="editingRowIndex = -1">
                                <b-icon icon="x-lg" variant=""></b-icon>
                            </a>
                        </div>
                    </slot>
                </template>
                <template #cell(created)="data">
                    {{ data.value }}
                    <div class="text-muted">
                        {{ data.item.createdBy }}
                    </div>
                </template>
                <template #cell(tags)="data">
                    <multiselect v-if="editingRowIndex === data.index" v-model="editingPatientDocument.tags"
                        :options="availableTags" multiple />
                    <div v-else class="d-flex flex-wrap" style="gap: 5px;">
                        <b-badge v-for="i in data.value" :key="i" variant="primary">
                            {{ i }}
                        </b-badge>
                    </div>
                </template>
            </b-table>
            <BottomPagerBar v-if="taskCreation"
                :selected-rows="selectedImageIds.length"
                :total-rows="patientDocuments.length"
                :filtered-rows="filterTags.length ? filteredPatientDocuments.length : undefined"
                item-name="document"
                show-total hide-pager
                @clear-selection="selectedImageIds = []">
                <template v-if="selectedImageIds.length" #fab>
                    <b-button size="sm" variant="primary" @click="createTaskFromSelected">
                        <i class="fa fa-check-square mr-1" />
                        Create Task
                    </b-button>
                </template>
            </BottomPagerBar>
        </b-col>
    </b-row>
</template>

<script lang="ts">
import axios from 'axios';
import moment from "moment";
import { toRaw } from 'vue';
import { Component, Emit, Prop, Ref, Vue, Watch } from 'vue-property-decorator';
import { NotificationOptions } from '@/util/NotificationOptionsPresets';
import SearchComponent from '@/components/SearchComponent.vue';
import Multiselect from "vue-multiselect";
import { QueueItem } from '@/models/QueueItem';
import { PatientDocument } from '@/models/PatientDocument';
import { Patient } from '@/models/Patient';
import { AcceptanceConfiguration } from '@/models/AcceptanceConfiguration';
import { AcceptanceSource } from '@/models/AcceptanceSource';
import AcceptanceConfigSelector from "@/components/Acceptance/AcceptanceConfigSelector.vue";
import PatientFormComponent from "@/components/Patient/PatientFormComponent.vue";
import PatientRecentlyViewed from "@/components/Patient/PatientRecentlyViewed.vue";
import BottomPagerBar from "@/components/BottomPagerBar.vue";
import PdfAnnotationModal from "@/components/PdfAnnotationModal.vue";
import PdfChopperModal from "@/components/Prescription/PdfChopperModal.vue";
import TaskDetails from "@/components/Tasks/TaskDetails.vue";
import TextUtils from '@/util/TextUtils';
import FaxPreviewModal from "@/components/Fax/FaxPreviewModal.vue";

@Component({
    name: "PatientDocumentsForm",
    components: {
        AcceptanceConfigSelector,
        BottomPagerBar,
        PatientFormComponent,
        PatientRecentlyViewed,
        SearchComponent,
        Multiselect,
        PdfAnnotationModal,
        PdfChopperModal,
        TaskDetails,
        FaxPreviewModal,
    },
})
export default class PatientDocumentsForm extends Vue {
    @Ref("previewModal") private previewModal!: FaxPreviewModal;
    @Prop({ default: 'patient_documents_modal' }) modalId!: string;
    @Prop({
        default: false,
        type: Boolean,
    }) listOnly!: boolean;
    @Prop() private patient!: Patient;
    @Prop() private queueItem!: QueueItem | null;
    @Prop({
        default: false,
        type: Boolean,
    }) private selectable!: boolean;
    @Prop({
        default: false,
        type: Boolean,
    }) private taskCreation!: boolean;
    @Prop({
        default: false,
        type: Boolean,
    }) private taskOnSave!: boolean;
    @Prop({
        default: false,
        type: Boolean,
    }) private allowProcessAsPrescription!: boolean;
    private selectedPatient: Patient = new Patient();
    private newDocument: File = new File([], "");
    private fileName: string = '';
    private isUploading = false;
    private patientDocuments = [] as PatientDocument[];
    private selectedImageIds: number[] = [];
    private availableTags: string[] = [];
    private selectedTags: string[] = [];
    private filterTags: string[] = [];
    private editingRowIndex = -1;
    private editingPatientDocument = new PatientDocument();
    protected showPatientForm = false;
    private faxNumber: string = "";
    private faxImageId: number = 0;
    private faxNote: string = "";
    private requirePatientDocumentTags = false;
    private annotatingDocument: PatientDocument | null = null;
    private splittingDocument: PatientDocument | null = null;
    private showSplitModal = false;
    private showDeleted = false;
    private forwardViaFax = false;
    private uploadFaxNumber: string = "";
    private uploadFaxNote: string = "";
    private documentToMove: PatientDocument | null = null;
    private moveTargetPatient: Patient | null = null;
    private hideMoveRecentList = true;
    private createAcceptanceOnSave = false;
    private selectedAcceptanceConfigObject: AcceptanceConfiguration | null = null;
    private processAsPrescription = false;
    private acceptanceSourceType: AcceptanceSource | null = null;
    private showCreateTaskModal = false;
    private createTaskDocuments: PatientDocument[] = [];
    private createTaskOnSave = false;
    private deferredSaveEmit: (() => void) | null = null;

    protected patientButtonTitles = {
        new: "",
        clear: "",
        save: "Save Patient",
        cancel: "Close Patient Form",
    };

    closePatientForm() {
        this.showPatientForm = false;
        this.selectedPatient = new Patient();
    }

    onPatientCreated(newPatient: Patient) {
        this.showPatientForm = false;
        this.selectedPatient = newPatient;
        this.$bvModal.hide("create-patient");
    }

    get fields(): any[] {
        const resp = [];
        if (this.selectable || this.taskCreation) {
            resp.push({
                label: "",
                key: "selected",
                class: "select-col",
            });
        }
        resp.push(
            {
                label: "File Name",
                key: "fileName",
                sortable: true,
                class: "file-name",
            },
            {
                key: "tags",
                hidden: !this.availableTags.length,
                class: "tags",
            },
            {
                label: "Uploaded",
                key: "created",
                sortable: true,
                formatter: (d: any) => moment.utc(d).local().format("L LT"),
                class: "uploaded",
            },
            {
                key: "view",
                label: "",
                class: "actions",
            },
        );
        return resp.filter(f => f.hidden !== true);
    }

    get documentsHaveTags() {
        return this.patientDocuments.some(pd => pd.tags?.length);
    }

    get documentsUniqueTags() {
        const tags: string[] = [];

        this.patientDocuments.forEach(pd => {
            pd.tags?.forEach(t => {
                const tag = t.toLowerCase().trim();
                if (tags.includes(tag)) return;
                tags.push(tag);
            });
        });

        tags.sort();

        return tags;
    }

    get isValidUploadFaxNumber(): boolean {
        if (!this.forwardViaFax) return true;
        const faxNumber = this.uploadFaxNumber.replace(/\D/g, '');
        // Match TextUtils.formatFaxNumber logic: 10 digits or 11 digits starting with 1
        return faxNumber.length === 10 || (faxNumber.length === 11 && faxNumber.startsWith('1'));
    }

    // The acceptance configuration's id, derived from the selected object so the id is never
    // a second source of truth that can drift from the object the selector binds.
    private get selectedAcceptanceConfigId(): number | null {
        return this.selectedAcceptanceConfigObject?.id ?? null;
    }

    get disableSave() {
        // Check if tags are required and none selected
        const tagsRequired = this.requirePatientDocumentTags && this.availableTags.length > 0;
        const hasRequiredTags = !tagsRequired || this.selectedTags.length > 0;

        // Validate fax number if forwarding via fax
        const faxValid = this.isValidUploadFaxNumber;

        // Require acceptance config when create acceptance is checked
        const acceptanceValid = !this.createAcceptanceOnSave || !!this.selectedAcceptanceConfigId;

        if (this.mode == 'new') {
            return !(this.newDocument.size > 0 && this.isValidFileName && hasRequiredTags && faxValid && acceptanceValid);
        } else if (this.mode == 'associate') {
            return !(this.patientID && this.isValidFileName && this.queueItem?.id && hasRequiredTags && acceptanceValid);
        }

        return true;
    }

    get emptyText(): string {
        return this.patient?.id ? 'No documents for this patient.' : 'Select a patient.';
    }

    get patientID(): string | number | null {
        return this.patient?.id || this.selectedPatient?.id;
    }

    // In 'associate' mode the actually-selected patient lives in selectedPatient, not the
    // (absent) patient prop — mirror the patientID fallback so the chopper's "Patient Document"
    // destination carries the right patient instead of an empty one.
    get splitPatient(): Patient {
        return this.selectedPatient?.id ? this.selectedPatient : this.patient;
    }

    get mode(): string | null {
        if (!this.patient?.id && this.queueItem) {
            return 'associate';
        } else if (this.patient?.id && !this.queueItem) {
            return 'new';
        } else {
            return null;
        }
    }

    get isValidFileName(): boolean {
        const regex = /^[\w,\s-]+$/;
        const validFileName: boolean = regex.test(this.fileName?.trim());
        return validFileName;
    }

    get fileNameInputClass(): string {
        const val = this.isValidFileName;
        return val ? "has-success" : "has-danger";
    }

    get filteredPatientDocuments() {
        let docs = this.patientDocuments;

        // Filter out deleted documents unless showDeleted is true
        if (!this.showDeleted) {
            docs = docs.filter(pd => !pd.deleted);
        }

        // Filter by tags if any selected
        if (this.filterTags.length) {
            docs = docs.filter(pd => pd.tags?.some(t => this.filterTags.includes(t.toLowerCase().trim())));
        }

        return docs;
    }

    get tagsRequired() {
        return this.requirePatientDocumentTags && this.availableTags.length > 0;
    }

    get hasDeletePermission() {
        return this.$permissions.some(p => p.permissionCodeName?.toLowerCase() === "patient dashboard/delete patient documents");
    }

    get hasViewDeletedPermission() {
        return this.$permissions.some(p => p.permissionCodeName?.toLowerCase() === "patient dashboard/view deleted patient documents");
    }

    get hasMovePermission() {
        return this.$permissions.some(p => p.permissionCodeName?.toLowerCase() === "patient dashboard/move patient documents");
    }

    get hasAnnotateOrEditPermission() {
        return this.$permissions.some(p =>
            p.permissionCodeName === "Maintenance/Hardcopy Note" ||
            p.permissionCodeName === "Maintenance/Edit Image"
        );
    }

    get deletedDocumentsCount() {
        return this.patientDocuments.filter(pd => pd.deleted).length;
    }

    async created() {
        //console.log('Pat docs form created');
        this.getTags();
        this.loadDocs();
        this.getConfiguration();
    }

    @Watch("selectedImageIds")
    @Emit("selectedImageIdsUpdate")
    updateIds(newValue: number[], _oldValue: number[]) {
        return newValue;
    }

    @Watch('patientID')
    async patientIdChanged() {
        // Reset acceptance config when patient changes
        this.selectedAcceptanceConfigObject = null;
        await this.loadDocs();
    }

    async loadDocs() {
        if (!this.patientID) return;

        try {
            // Always load deleted docs if user has permission, so we can count them for the toggle
            const params = this.hasViewDeletedPermission ? { includeDeleted: true } : {};
            const res = await axios.get<Array<PatientDocument>>(`/patient/${this.patientID}/get-documents/`, { params });
            if (res.data?.length) {
                this.patientDocuments = res.data.map(doc => new PatientDocument(doc))
                    .sort((a, b) => new Date(b.created).getTime() - new Date(a.created).getTime());
            } else {
                this.patientDocuments = [];
            }
        } catch (err) {
            console.error("Error while loading patient documents.", err);
            this.$notification(NotificationOptions.error(err));
        }
    }

    async submit() {
        if (this.mode == 'associate') {
            await this.associateDocument();
        } else if (this.mode == 'new') {
            await this.saveDocument();
        }
    }

    async saveDocument() {
        if (this.disableSave) return;

        if (this.createAcceptanceOnSave && !this.selectedAcceptanceConfigId) {
            this.$notification(NotificationOptions.warning(
                "Please select an acceptance configuration or uncheck the acceptance record option."
            ));
            return;
        }

        if (this.createAcceptanceOnSave && this.acceptanceSourceType === null) {
            this.$notification(NotificationOptions.warning(
                "Please select a source type or uncheck the acceptance record option."
            ));
            return;
        }

        // Upload custom table if there is any
        const formData = new FormData();

        formData.append('document', this.newDocument);
        formData.append('fileName', this.fileName.trim());
        formData.append('tags', JSON.stringify(this.selectedTags));
        const errorMsg = "Error while uploading new patient's document.";

        this.isUploading = true;
        try {
            const response = await axios.post<PatientDocument>(`/patient/${this.patientID}/add-document/`
                , formData, { headers: { 'Content-Type': 'multipart/form-data' } });
            if (response.data?.id) {
                let notificationShown = false;

                // Create acceptance if checkbox was selected
                if (this.createAcceptanceOnSave && this.selectedAcceptanceConfigId && response.data.imageId) {
                    try {
                        await axios.post('AcceptanceRecord/with-document', {
                            configurationId: this.selectedAcceptanceConfigId,
                            patientId: this.patientID,
                            imageId: response.data.imageId,
                            sourceType: this.acceptanceSourceType,
                            declined: false
                        });
                        this.$notification(NotificationOptions.success("Document saved and acceptance record created"));
                        notificationShown = true;
                    } catch (acceptErr) {
                        console.error("Failed to create acceptance", acceptErr);
                        this.$notification(NotificationOptions.notificationOptionsPreset(
                            "Document saved but failed to create acceptance record",
                            "warning"
                        ));
                        notificationShown = true;
                    }
                }

                // Send fax if checkbox is checked
                if (this.forwardViaFax && response.data.imageId) {
                    try {
                        await axios.post("api/Fax/Send", {
                            imageId: response.data.imageId,
                            faxNumber: TextUtils.formatFaxNumber(this.uploadFaxNumber),
                            note: this.uploadFaxNote,
                            patientId: this.patientID,
                        });
                        this.$notification(NotificationOptions.success("Document saved and fax sent successfully."));
                        notificationShown = true;
                    } catch (faxErr: any) {
                        console.error("Fax send failed", faxErr);
                        const apiMessage = faxErr?.response?.data;
                        const detail = typeof apiMessage === 'string' && apiMessage.length
                            ? `: ${apiMessage}`
                            : "";
                        this.$notification(NotificationOptions.warning(
                            `Document saved but fax failed to send${detail}`
                        ));
                        notificationShown = true;
                    }
                }
                this.resetForm(response.data, notificationShown);
                this.loadDocs();
            } else {
                console.error(errorMsg, response);
            }
        } catch (err) {
            console.error(errorMsg, err);
            this.$notification(NotificationOptions.error(err));
        } finally {
            this.isUploading = false;
        }
    }

    async associateDocument() {
        if (this.disableSave) return;

        if (this.createAcceptanceOnSave && !this.selectedAcceptanceConfigId) {
            this.$notification(NotificationOptions.warning(
                "Please select an acceptance configuration or uncheck the acceptance record option."
            ));
            return;
        }

        if (this.createAcceptanceOnSave && this.acceptanceSourceType === null) {
            this.$notification(NotificationOptions.warning(
                "Please select a source type or uncheck the acceptance record option."
            ));
            return;
        }

        const errorMsg = "Error while associating selected document to the patient.";
        const pd = new PatientDocument();
        pd.fileName = this.fileName.trim();
        pd.tags = this.selectedTags;

        this.isUploading = true;

        try {
            const response = await axios.post<PatientDocument>(`/patient/${this.patientID}/associate-document/${this.queueItem?.id}`, pd);
            if (response.data?.id) {
                let notificationShown = false;

                // Create acceptance if checkbox was selected
                if (this.createAcceptanceOnSave && this.selectedAcceptanceConfigId && response.data.imageId) {
                    try {
                        await axios.post('AcceptanceRecord/with-document', {
                            configurationId: this.selectedAcceptanceConfigId,
                            patientId: this.patientID,
                            imageId: response.data.imageId,
                            sourceType: this.acceptanceSourceType,
                            declined: false
                        });
                        this.$notification(NotificationOptions.success("Document saved and acceptance record created"));
                        notificationShown = true;
                    } catch (acceptErr) {
                        console.error("Failed to create acceptance", acceptErr);
                        this.$notification(NotificationOptions.notificationOptionsPreset(
                            "Document saved but failed to create acceptance record",
                            "warning"
                        ));
                        notificationShown = true;
                    }
                }

                this.resetForm(response.data, notificationShown, true);
            } else {
                console.error(errorMsg, response);
            }
        } catch (err) {
            console.error(errorMsg, err);
            this.$notification(NotificationOptions.error(err));
        } finally {
            this.isUploading = false;
        }
    }

    // hideModalWhenDone dismisses the enclosing documents modal as part of completing the save, so
    // it stays standing behind the task modal for as long as the save event is withheld.
    resetForm(resData: PatientDocument, notificationAlreadyShown = false, hideModalWhenDone = false) {
        if (!notificationAlreadyShown) {
            this.$notification(NotificationOptions.successSaveNotificationPreset('Patient Document'));
        }
        const shouldProcessAsPrescription = this.processAsPrescription;
        const shouldCreateTask = this.createTaskOnSave && !!resData?.id;
        this.newDocument = new File([], "");
        this.fileName = '';
        this.forwardViaFax = false;
        this.uploadFaxNumber = '';
        this.uploadFaxNote = '';
        this.createAcceptanceOnSave = false;
        this.selectedAcceptanceConfigObject = null;
        this.processAsPrescription = false;
        this.acceptanceSourceType = null;
        this.createTaskOnSave = false;

        const finishSave = () => {
            if (shouldProcessAsPrescription) {
                this.$emit('process-as-prescription', resData);
            } else {
                this.$emit('on-save', resData);
            }
            if (hideModalWhenDone) {
                this.$bvModal.hide(this.modalId);
            }
        };

        if (shouldCreateTask) {
            // Rx Intake advances its queue and navigates away the moment 'on-save' lands, which
            // would strip this screen before the task can be written. Hold the event until the
            // task modal settles.
            this.deferredSaveEmit = finishSave;
            this.createTaskForDocument(resData);
            return;
        }

        finishSave();
    }

    async faxDocument() {
        try {
            await axios.post("api/Fax/Send", {
                imageId: this.faxImageId,
                faxNumber: TextUtils.formatFaxNumber(this.faxNumber),
                note: this.faxNote,
                patientId: this.patientID,
            });
            this.$notification(NotificationOptions.success("Fax sent successfully."));
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
        }
    }

    // Renders the exact PDF that faxDocument would transmit (document + optional note cover page)
    // without sending it.
    previewFaxDocument() {
        this.previewModal.open(async () => {
            const res = await axios.post("api/Fax/preview", {
                imageId: this.faxImageId,
                faxNumber: TextUtils.formatFaxNumber(this.faxNumber),
                note: this.faxNote,
                patientId: this.patientID,
            }, { responseType: "blob" });
            return res.data;
        });
    }

    clearFaxForm() {
        this.faxNumber = '';
        this.faxNote = '';
    }

    async openDocument(pd: PatientDocument) {
        try {
            const patientId = this.patientID;
            if (!patientId) {
                this.$notification(NotificationOptions.error("No patient selected"));
                return;
            }

            const res = await axios.get(`patient/${patientId}/document/${pd.imageId}`, { responseType: "blob" });
            const objectUrl = URL.createObjectURL(res.data);
            window.open(objectUrl, '_blank');
            setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
        } catch (err) {
            console.error("Error while getting document to show.", err);
            this.$notification(NotificationOptions.error(err));
        }
    }

    async getTags() {
        try {
            const response = await axios.get("/Configuration/PatientDocumentTags");
            this.availableTags = response.data;
        } catch (err) {
            console.error(err);
        }
    }

    async getConfiguration() {
        try {
            const response = await axios.get("/Configuration/RequirePatientDocumentTags");
            this.requirePatientDocumentTags = response.data;
        } catch (err) {
            console.error(err);
        }
    }

    clearFilters() {
        this.filterTags = [];
        this.scrollToTable();
    }

    toggleFilterTag(tag: string) {
        if (this.filterTags.includes(tag)) {
            this.filterTags = this.filterTags.filter(ff => ff !== tag);
        } else {
            this.filterTags.push(tag);
        }
        this.scrollToTable();
    }

    scrollToTable() {
        this.$nextTick(() => {
            (this.$refs.documentsTable as any)?.$el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
    }

    getDocumentCountForTag(tag: string) {
        return this.patientDocuments
                   .filter(pd => pd.tags?.some(t => t.toLowerCase().trim() === tag)).length;
    }

    startEditDocument(item: PatientDocument, index: number) {
        this.editingPatientDocument = structuredClone(toRaw(item));
        this.editingRowIndex = index;
    }

    async editDocument() {
        try {
            await axios.post("/patient/edit-document", this.editingPatientDocument);

            this.loadDocs();
            this.editingRowIndex = -1;

        } catch (err) {
            console.error("Failed to edit patient document.", err);
            this.$notification(NotificationOptions.error(err));
        }
    }

    openAnnotateModal(document: PatientDocument) {
        this.annotatingDocument = document;
        this.$nextTick(() => {
            this.$bvModal.show('patient-document-annotation');
        });
    }

    async onAnnotationSaved(payload: { oldImageId: number; newImageId: number }) {
        if (this.annotatingDocument && payload.newImageId) {
            try {
                // Update the document with the new imageId
                const updatedDoc = {
                    ...this.annotatingDocument,
                    imageId: payload.newImageId,
                };
                await axios.post("/patient/edit-document", updatedDoc);

                this.$notification(NotificationOptions.success("Document annotated and saved successfully."));
                this.loadDocs();
            } catch (err) {
                console.error("Failed to update patient document with annotated image.", err);
                this.$notification(NotificationOptions.error(err));
            }
        }
        this.annotatingDocument = null;
    }

    openSplitModal(document: PatientDocument) {
        this.splittingDocument = document;
        this.$nextTick(() => {
            this.showSplitModal = true;
        });
    }

    async onSplitComplete() {
        this.$notification(NotificationOptions.success("Document split successfully."));
        await this.loadDocs();
    }

    // The chopper modal's v-model flips to false when it closes (complete or cancel); tear down the
    // rendered instance so the next open re-fetches fresh pages for the newly selected document.
    @Watch("showSplitModal")
    onSplitModalVisibilityChanged(visible: boolean) {
        if (!visible) {
            this.splittingDocument = null;
        }
    }

    createTaskFromSelected() {
        this.createTaskDocuments = this.patientDocuments.filter(
            d => this.selectedImageIds.includes(d.imageId)
        );
        this.showCreateTaskModal = true;
    }

    createTaskForDocument(doc: PatientDocument) {
        this.createTaskDocuments = [doc];
        this.showCreateTaskModal = true;
    }

    onTaskCreated() {
        this.showCreateTaskModal = false;
        this.createTaskDocuments = [];
        this.selectedImageIds = [];
        this.$notification(NotificationOptions.success("Task created successfully."));
        this.finishDeferredSave();
    }

    onTaskModalClosed() {
        this.showCreateTaskModal = false;
        this.createTaskDocuments = [];
        this.finishDeferredSave();
    }

    // Idempotent: the task modal can report both 'saved' and 'closed', and the grid-launched task
    // flows leave nothing deferred at all. Dismissing without saving still lets the save through --
    // the document is already written and the queue item already complete.
    private finishDeferredSave() {
        const finishSave = this.deferredSaveEmit;
        this.deferredSaveEmit = null;
        finishSave?.();
    }

    close() {
        this.$emit('close');
    }

    rowClass(item: PatientDocument) {
        if (item.deleted) {
            return 'deleted-row';
        }
        return '';
    }

    async confirmDeleteDocument(doc: PatientDocument) {
        const confirm = await this.$bvModal.msgBoxConfirm(
            `Are you sure you want to delete "${doc.fileName}"?`,
            {
                title: 'Delete Document',
                okVariant: 'danger',
                okTitle: 'Delete',
                centered: true,
            }
        );
        if (!confirm) return;

        try {
            await axios.delete(`/patient/${this.patientID}/document/${doc.id}`);
            this.$notification(NotificationOptions.success("Document deleted successfully."));
            await this.loadDocs();
        } catch (err) {
            console.error("Failed to delete patient document.", err);
            this.$notification(NotificationOptions.error(err));
        }
    }

    openMoveModal(doc: PatientDocument) {
        this.documentToMove = doc;
        this.$bvModal.show('move-doc-modal');
    }

    showMoveModal() {
        this.moveTargetPatient = new Patient();
        this.hideMoveRecentList = true;
    }

    async moveDocument() {
        if (!this.documentToMove || !this.moveTargetPatient?.id) return;

        try {
            await axios.post(`/patient/document/move`, {
                patientId: this.patientID,
                documentId: this.documentToMove.id,
                targetPatientId: this.moveTargetPatient.id
            });
            this.$notification(NotificationOptions.success(`Document moved to ${this.moveTargetPatient.displayNamesForPerson()}.`));
            this.documentToMove = null;
            this.moveTargetPatient = null;
            await this.loadDocs();
        } catch (err) {
            console.error("Failed to move patient document.", err);
            this.$notification(NotificationOptions.error(err));
        }
    }
}

</script>

<style scoped>
.documents-table {
    scroll-margin-top: 60px;
}

::v-deep(table .select-col) {
    width: 40px;
}

::v-deep(table .actions) {
    width: 150px;
}

::v-deep(table .uploaded) {
    width: 260px;
}

::v-deep(table .file-name) {
    width: 40%;
}

::v-deep(table .tags) {
    width: 60%;
}


::v-deep(.badge-secondary) {
    color: rgb(58, 58, 58);
}

::v-deep(table input) {
    background: white;
}

.field-filters {
    gap: 5px;
    z-index: 10;
}

.field-filters .badge {
    cursor: pointer;
    font-size: .85em;
}

.field-filters .badge .count {
    font-weight: 500;
    font-size: .9em;
    font-variant-numeric: tabular-nums;
    margin-left: 3px;
}

.show-deleted-switch {
    font-size: .85em;
    align-self: center;
}

::v-deep(.deleted-row) {
    opacity: 0.6;
    background-color: #f8d7da !important;
}

/* Document actions dropdown styling */
.doc-actions-dropdown {
    margin-left: -3px;
}

::v-deep(.doc-actions-dropdown .btn) {
    padding: 3px 5px;
    line-height: 1;
    border: none;
    box-shadow: none !important;
}

::v-deep(.doc-actions-dropdown .btn:hover) {
    background: transparent;
}

::v-deep(.doc-actions-dropdown .dropdown-menu) {
    min-width: 200px;
}

::v-deep(.doc-actions-dropdown .dropdown-item) {
    display: flex;
    align-items: center;
}

.doc-actions-ellipsis {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    border: 1.5px solid #2196F3;
    border-radius: 50%;
    transition: background-color 0.15s ease;
}

.doc-actions-ellipsis:hover {
    background-color: rgba(33, 150, 243, 0.1);
}
</style>
